import React from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Typography,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  CardContent,
  Avatar,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  Backdrop,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  ErrorOutline as ErrorIcon,
  Undo as UndoIcon,
  CheckCircle as CheckIcon,
  Visibility as VisibilityIcon,
  LocalShipping as LocalShippingIcon,
  Edit as EditIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { ProductHistorySection } from './ProductHistorySection';
import { SaleFulfillmentTimeline } from '../SaleFulfillment/SaleFulfillmentTimeline';
import { EditSaleDateModal } from '../EditSaleDateModal';
import { DetailDrawer } from '../../../ui/Modal';
import InventoryDetailModal from '../../../inventory/InventoryDetailModal';
import { useOrderDetailsModal } from './OrderDetailsModal.hooks';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { SalesOrdersService } from '../../../../api';
import type { SaleDateHistoryEntryDto } from '../../../../api';
import { useEditSaleDate } from '../../../../hooks/useEditSaleDate';
import type { InventoryItem } from '../../../../types/inventory';
import { mapWarehouseInventoryProductApiToInventoryItem } from '../../../../utils/inventory/mapWarehouseInventoryProductToInventoryItem';
import {
  saleContextFromOrderItem,
  buildInventoryDetailSaleContext,
} from '../../../../utils/inventory/inventoryDetailSaleContext';
import * as S from './OrderDetailsModal.styles';
import { ViewButton, StatusChip } from '../../../common/Table/Table.styles';
import {
  commercialSaleDateDiffersFromRegistration,
  formatCommercialSaleDate,
  formatRegistrationDateTime,
} from '../../../../utils/sales/commercial-sale-date';
import {
  getSalesOrderCustomerDetailLabel,
  getSalesOrderCustomerPhoneDisplay,
  hasSalesOrderCustomer,
} from '../../../../utils/sales/sales-order-customer';
import { getSaleInvoiceLabel } from '../../../../utils/sales/sale-invoice.utils';
import { capitalizeSentence } from '../../../../utils/formatters/text';
import { BRANCH_RECEIPT_NUMBERING_ENABLED } from '../../../../config/feature-flags';
import {
  BRANCH_RECEIPT_NUMBER_LABEL,
  SALES_ORDER_NUMBER_LABEL,
} from '../../../../utils/sales/branch-receipt-numbering';
import { useOrdersPermissions } from '../../../../hooks/useOrdersPermissions';
import {
  canProcessSalesOrderPayment,
  canUpdateSalesOrderDelivery,
  canEditSalesOrderSaleDate,
} from '../../../../utils/sales/sales-order-actions';
import { useSaleFulfillmentDetail } from '../../../../hooks/useSaleFulfillmentDetail';
import { salesApi } from '../../../../services/sales.service';
import {
  getOrderFulfillmentPresentation,
  getFulfillmentProgressCaption,
} from '../../../../utils/sales/saleFulfillmentPresentation';
import {
  getDeliverableUndeliveredItems,
  getItemDeliveryChipState,
  getItemOriginLocationName,
  getItemPresenceChipState,
} from '../../../../utils/sales/order-delivery.utils';
import { DeliverPiecesModal } from '../DeliverPiecesModal';
import { mapOrderDeliveryStatus } from '../../../common/status/statusMap';
import { SaleFulfillmentGroup, SaleFulfillmentSummary } from '../../../../types/sales.types';
import { getDisplayablePaymentNotes } from '../../../../utils/paymentDisplayNotes.utils';

/** Progress counters derived client-side from the loaded fulfillment groups. */
const buildFulfillmentSummary = (
  groups: SaleFulfillmentGroup[] | undefined,
): SaleFulfillmentSummary | null => {
  if (!groups || groups.length === 0) return null;
  // Groups from the fulfillment detail API already carry effective stage.
  return groups.reduce<SaleFulfillmentSummary>(
    (acc, g) => {
      acc.total += 1;
      const stage = g.status;
      if (stage === 'RECEIVED') acc.received += 1;
      else if (stage === 'IN_TRANSIT') acc.inTransit += 1;
      else if (stage === 'FAILED') acc.failed += 1;
      else acc.pending += 1;
      return acc;
    },
    { total: 0, received: 0, inTransit: 0, pending: 0, failed: 0 },
  );
};

interface OrderDetailsModalProps {
  open: boolean;
  onClose: () => void;
  orderId: number | null;
  onEditOrder?: (orderId: number) => void;
  onProcessPayment?: (orderId: number) => void;
  onGenerateInstallments?: (orderId: number) => void; // ⚠️ INACTIVO: No hay botón que use esta función
  onPrintReceipt?: (orderId: number) => void;
  onCancelOrder?: (orderId: number) => void;
  refreshTrigger?: number; // Trigger to refresh order details
  /** Re-run parent list with current applied filters after status/delivery mutations. */
  onOrdersMutated?: () => void;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  open,
  onClose,
  orderId,
  onEditOrder,
  onProcessPayment,
  onGenerateInstallments,
  onPrintReceipt,
  onCancelOrder,
  refreshTrigger,
  onOrdersMutated,
}) => {
  const ordersPerms = useOrdersPermissions();

  const {
    order,
    installments,
    isLoading,
    error,
    handleAction,
    refreshOrderDetails,
    getStatusChip,
    getPaymentMethodLabel,
    formatCurrency,
    formatDate,
    calculateFinancialDashboard,
    calculateProductDiscount,
    formatDiscountDisplay,
    formatMarkupDisplay,
    hasProductMarkup,
  } = useOrderDetailsModal({
    open,
    orderId,
    onEditOrder,
    onProcessPayment,
    onGenerateInstallments,
    onPrintReceipt,
    onCancelOrder,
  });

  const isMixedSale = order != null && order.fulfillmentStatus !== 'NOT_REQUIRED';
  const {
    detail: fulfillmentDetail,
    isLoading: isFulfillmentLoading,
    error: fulfillmentError,
    fetchDetail: refetchFulfillmentDetail,
  } = useSaleFulfillmentDetail(open ? orderId : null, {
    // Start as soon as complete sets order (not after payments/installments).
    // Cache + explicit refetch after mutations keeps drawer/list in sync.
    autoFetch: isMixedSale && open,
  });

  // Sales order item ID → SOL request code, derived from the fulfillment groups
  // (used by the "Solicitud" column in the Piezas table below).
  const requestCodeByItemId = useMemo(() => {
    const map = new Map<number, string>();
    for (const group of fulfillmentDetail?.groups ?? []) {
      if (!group.inventoryRequest) continue;
      for (const item of group.items) {
        map.set(item.id, group.inventoryRequest.requestCode);
      }
    }
    return map;
  }, [fulfillmentDetail]);

  const [isDelivering, setIsDelivering] = useState(false);
  const [deliverError, setDeliverError] = useState<string | null>(null);
  const [showDeliverModal, setShowDeliverModal] = useState(false);

  const [editSaleDateOpen, setEditSaleDateOpen] = useState(false);
  const [saleDateHistory, setSaleDateHistory] = useState<SaleDateHistoryEntryDto[]>([]);
  const [saleDateHistoryError, setSaleDateHistoryError] = useState<string | null>(null);

  const loadSaleDateHistory = useCallback(async (id: number) => {
    try {
      const rows = await salesApi.getSaleDateHistory(id);
      setSaleDateHistory(rows ?? []);
      setSaleDateHistoryError(null);
    } catch (err: unknown) {
      console.error(`Error loading sale date history for order ${id}:`, err);
      setSaleDateHistory([]);
      setSaleDateHistoryError('No se pudo cargar el historial de fecha de venta.');
    }
  }, []);

  const handleSaleDateSavedFromDetails = useCallback(
    async (orderId: number) => {
      setEditSaleDateOpen(false);
      await refreshOrderDetails();
      await loadSaleDateHistory(orderId);
      onOrdersMutated?.();
    },
    [refreshOrderDetails, loadSaleDateHistory, onOrdersMutated],
  );

  const editSaleDateAction = useEditSaleDate(handleSaleDateSavedFromDetails);
  const {
    loading: editSaleDateLoading,
    error: editSaleDateError,
    clearError: clearEditSaleDateError,
    save: saveEditSaleDate,
  } = editSaleDateAction;

  useEffect(() => {
    if (open && orderId != null) {
      void loadSaleDateHistory(orderId);
    } else {
      setSaleDateHistory([]);
      setSaleDateHistoryError(null);
      setEditSaleDateOpen(false);
      clearEditSaleDateError();
    }
  }, [open, orderId, loadSaleDateHistory, clearEditSaleDateError]);

  const handleSaveSaleDate = useCallback(
    async (saleDateIso: string) => {
      if (order == null) return;
      await saveEditSaleDate(order.id, saleDateIso);
    },
    [order, saveEditSaleDate],
  );

  const handleDeliverPieces = useCallback(
    async (items: Array<{ itemId: number; isDelivered: boolean }>) => {
      if (order == null) return;
      setIsDelivering(true);
      setDeliverError(null);
      try {
        await salesApi.updateOrderDelivery(order.id, items);
        setShowDeliverModal(false);
        await Promise.all([refreshOrderDetails(), refetchFulfillmentDetail()]);
        onOrdersMutated?.();
      } catch (err: unknown) {
        const message =
          (err as { body?: { message?: string } })?.body?.message ||
          (err instanceof Error ? err.message : 'No se pudo registrar la entrega.');
        setDeliverError(message);
      } finally {
        setIsDelivering(false);
      }
    },
    [order, refreshOrderDetails, refetchFulfillmentDetail, onOrdersMutated],
  );

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger != null && refreshTrigger > 0 && open && orderId != null) {
      refreshOrderDetails();
      void refetchFulfillmentDetail();
    }
  }, [refreshTrigger, open, orderId, refreshOrderDetails, refetchFulfillmentDetail]);

  const pieceDetailContextRef = useRef<{
    orderId: number;
    wipId: number;
    historyFinalPrice?: number;
  } | null>(null);
  const [pieceDetailOpen, setPieceDetailOpen] = useState(false);
  const [pieceDetailItem, setPieceDetailItem] = useState<InventoryItem | null>(null);
  const [pieceDetailFetching, setPieceDetailFetching] = useState(false);
  const [pieceDetailFetchError, setPieceDetailFetchError] = useState<string | null>(null);

  const closePieceDetailModal = useCallback(() => {
    pieceDetailContextRef.current = null;
    setPieceDetailOpen(false);
    setPieceDetailItem(null);
    setPieceDetailFetchError(null);
    setPieceDetailFetching(false);
  }, []);

  const reloadPieceDetailFromOrder = useCallback(async () => {
    const ctx = pieceDetailContextRef.current;
    if (!ctx) return;
    try {
      const res =
        await SalesOrdersService.salesOrdersControllerFindWarehouseInventoryProductForOrder(
          ctx.orderId,
          ctx.wipId,
        );
      const data = res?.data ?? res;
      setPieceDetailItem(mapWarehouseInventoryProductApiToInventoryItem(data));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const openPieceDetail = useCallback(
    async (warehouseInventoryProductId: number, historyFinalPrice?: number) => {
      const oid = order?.id ?? orderId;
      if (oid == null || !warehouseInventoryProductId) return;
      pieceDetailContextRef.current = {
        orderId: oid,
        wipId: warehouseInventoryProductId,
        historyFinalPrice,
      };
      setPieceDetailFetchError(null);
      setPieceDetailItem(null);
      setPieceDetailOpen(false);
      setPieceDetailFetching(true);
      try {
        const res =
          await SalesOrdersService.salesOrdersControllerFindWarehouseInventoryProductForOrder(
            oid,
            warehouseInventoryProductId,
          );
        const data = res?.data ?? res;
        setPieceDetailItem(mapWarehouseInventoryProductApiToInventoryItem(data));
        setPieceDetailOpen(true);
      } catch (err: unknown) {
        console.error(err);
        const msg =
          (err as { body?: { message?: string } })?.body?.message ||
          (err as Error)?.message ||
          'No se pudo cargar el detalle de la pieza.';
        setPieceDetailFetchError(msg);
      } finally {
        setPieceDetailFetching(false);
      }
    },
    [order?.id, orderId],
  );

  useEffect(() => {
    if (!open) {
      closePieceDetailModal();
    }
  }, [open, closePieceDetailModal]);

  // pieceDetailItem in deps forces recalculation after the piece detail fetch fills the ref
  const pieceDetailSaleContext = useMemo(() => {
    const ctx = pieceDetailContextRef.current;
    if (ctx == null || order == null) return undefined;
    if (ctx.historyFinalPrice != null) {
      return buildInventoryDetailSaleContext({
        finalPrice: ctx.historyFinalPrice,
        saleDate: order.saleDate,
      });
    }
    return saleContextFromOrderItem(order.items, ctx.wipId, order.saleDate);
  }, [order, pieceDetailItem]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional refresh trigger

  const deliverableCount = useMemo(
    () => (order != null ? getDeliverableUndeliveredItems(order, fulfillmentDetail).length : 0),
    [order, fulfillmentDetail],
  );

  if (!open) return null;

  const financialData = calculateFinancialDashboard();
  const statusConfig = order ? getStatusChip(order.status) : null;
  const StatusIcon = statusConfig?.icon;
  const customerPhoneDisplay = order ? getSalesOrderCustomerPhoneDisplay(order.customer) : null;

  // Footer actions con botones estilo Tipo 1 y gradiente
  const showProcessPayment =
    order != null &&
    ordersPerms.canPay &&
    canProcessSalesOrderPayment(order.status) &&
    onProcessPayment != null;

  const showDeliverPieces =
    order != null &&
    ordersPerms.canEdit &&
    canUpdateSalesOrderDelivery(order.status, order.deliveryStatus) &&
    deliverableCount > 0;

  const showEditSaleDate =
    order != null && ordersPerms.canEditSaleDate && canEditSalesOrderSaleDate(order.status);

  const footerActions = (
    <>
      <S.CloseButton onClick={onClose}>Cerrar</S.CloseButton>
      {showProcessPayment && (
        <S.PrintButton
          variant="contained"
          startIcon={<PaymentIcon />}
          onClick={() => handleAction('payment')}
        >
          Procesar pago
        </S.PrintButton>
      )}
      {showDeliverPieces && (
        <S.PrintButton
          variant="contained"
          startIcon={
            isDelivering ? <CircularProgress size={16} color="inherit" /> : <LocalShippingIcon />
          }
          onClick={() => {
            setDeliverError(null);
            setShowDeliverModal(true);
          }}
          disabled={isDelivering}
        >
          Entregar piezas
        </S.PrintButton>
      )}
      {/* Reimpresión de comprobante: oculta hasta implementar (futuro). */}
    </>
  );

  return (
    <>
      <DetailDrawer
        open={open}
        onClose={onClose}
        title={
          order
            ? BRANCH_RECEIPT_NUMBERING_ENABLED && order.branchReceiptCode
              ? `Detalles de la venta: ${order.orderCode} · ${order.branchReceiptCode}`
              : `Detalles de la venta: ${order.orderCode}`
            : 'Detalles de la venta: —'
        }
        footerActions={footerActions}
      >
        {isLoading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}
          >
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : order && financialData ? (
          <Box>
            {/* Financial Dashboard */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <S.DashboardCardBlue variant="outlined">
                  <CardContent sx={{ py: 2.25, position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Avatar
                        variant="rounded"
                        sx={(theme) => ({
                          bgcolor: alpha(
                            theme.palette.primary.main,
                            theme.palette.mode === 'dark' ? 0.22 : 0.16,
                          ),
                          color:
                            theme.palette.mode === 'dark'
                              ? theme.palette.primary.light
                              : theme.palette.primary.main,
                          width: 36,
                          height: 36,
                        })}
                      >
                        <ReceiptIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="overline"
                          sx={(theme) => ({
                            letterSpacing: 0.4,
                            color:
                              theme.palette.mode === 'dark'
                                ? theme.palette.primary.light
                                : theme.palette.primary.main,
                          })}
                        >
                          Total de venta
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={(theme) => ({
                            fontWeight: 800,
                            lineHeight: 1.2,
                            color:
                              theme.palette.mode === 'dark'
                                ? theme.palette.primary.light
                                : theme.palette.primary.dark,
                          })}
                        >
                          {formatCurrency(financialData.orderTotal)}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 1.25, borderColor: 'divider' }} />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            flexWrap: 'nowrap',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', lineHeight: 1.4 }}
                          >
                            Fecha de venta:{' '}
                            {formatCommercialSaleDate(order, 'es-ES', { includeTime: true })}
                          </Typography>
                          {showEditSaleDate && (
                            <Tooltip title="Editar fecha y hora de venta">
                              <IconButton
                                size="small"
                                aria-label="Editar fecha y hora de venta"
                                onClick={() => {
                                  clearEditSaleDateError();
                                  setEditSaleDateOpen(true);
                                }}
                                sx={{
                                  p: 0.5,
                                  color: 'primary.main',
                                  border: 1,
                                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
                                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                  '&:hover': {
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                                    borderColor: 'primary.main',
                                  },
                                }}
                              >
                                <EditIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        {commercialSaleDateDiffersFromRegistration(order) && (
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block', opacity: 0.92 }}
                          >
                            Registrada en sistema: {formatRegistrationDateTime(order.createdAt)}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', display: 'block' }}
                        >
                          {SALES_ORDER_NUMBER_LABEL}:{' '}
                          <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                            {order.orderCode}
                          </Box>
                        </Typography>
                        {BRANCH_RECEIPT_NUMBERING_ENABLED && order.branchReceiptCode ? (
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block' }}
                          >
                            {BRANCH_RECEIPT_NUMBER_LABEL}:{' '}
                            <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                              {order.branchReceiptCode}
                            </Box>
                          </Typography>
                        ) : null}
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', display: 'block' }}
                        >
                          Cliente:{' '}
                          <Box
                            component="span"
                            sx={{
                              color: hasSalesOrderCustomer(order.customer)
                                ? 'primary.light'
                                : 'text.secondary',
                              fontWeight: hasSalesOrderCustomer(order.customer) ? 700 : 500,
                              fontStyle: hasSalesOrderCustomer(order.customer)
                                ? 'normal'
                                : 'italic',
                            }}
                          >
                            {getSalesOrderCustomerDetailLabel(order.customer)}
                          </Box>
                        </Typography>
                        {customerPhoneDisplay != null && (
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block' }}
                          >
                            Teléfono:{' '}
                            <Box
                              component="span"
                              sx={{
                                color: customerPhoneDisplay.hasPhone
                                  ? 'primary.light'
                                  : 'text.secondary',
                                fontWeight: customerPhoneDisplay.hasPhone ? 700 : 500,
                                fontStyle: customerPhoneDisplay.hasPhone ? 'normal' : 'italic',
                              }}
                            >
                              {customerPhoneDisplay.label}
                            </Box>
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', display: 'block' }}
                        >
                          Factura:{' '}
                          <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                            {getSaleInvoiceLabel(Boolean(order.hasInvoice))}
                          </Box>
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', display: 'block' }}
                        >
                          Sucursal:{' '}
                          <Box component="span" sx={{ color: 'primary.light', fontWeight: 700 }}>
                            {order.location.name}
                          </Box>
                        </Typography>
                      </Box>
                      {(order.status === 'PARTIAL_PAID' ||
                        order.status === 'PAID' ||
                        order.status === 'CONFIRMED' ||
                        order.status === 'DELIVERED' ||
                        order.status === 'CANCELLED' ||
                        order.status === 'REFUNDED') &&
                        StatusIcon && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.75,
                              alignItems: 'flex-end',
                            }}
                          >
                            <Chip
                              icon={<StatusIcon fontSize="small" />}
                              label={statusConfig?.label}
                              color={statusConfig?.color}
                              variant="filled"
                            />
                            {isMixedSale &&
                              (() => {
                                const presentation = getOrderFulfillmentPresentation(
                                  order.fulfillmentStatus,
                                );
                                if (!presentation) return null;
                                const summary = buildFulfillmentSummary(fulfillmentDetail?.groups);
                                const progress = getFulfillmentProgressCaption(
                                  order.fulfillmentStatus,
                                  summary,
                                );
                                return (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'flex-end',
                                      gap: 0.25,
                                    }}
                                  >
                                    <Tooltip title={presentation.helperText}>
                                      <span>
                                        <StatusChip
                                          label={presentation.label}
                                          status={presentation.statusType}
                                          visualStyle={presentation.visualStyle}
                                          withIcon={presentation.withIcon}
                                          size="small"
                                        />
                                      </span>
                                    </Tooltip>
                                    {progress && (
                                      <Typography variant="caption" color="text.secondary">
                                        {progress}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })()}
                            {(() => {
                              const deliveryChip = mapOrderDeliveryStatus(
                                order.deliveryStatus ?? 'NONE',
                              );
                              return (
                                <StatusChip
                                  label={deliveryChip.label}
                                  color={deliveryChip.color}
                                  size="small"
                                />
                              );
                            })()}
                          </Box>
                        )}
                    </Box>
                  </CardContent>
                </S.DashboardCardBlue>
              </Grid>
              <Grid item xs={12} md={3}>
                <S.DashboardCardGreen variant="outlined">
                  <CardContent sx={{ py: 2.25 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: financialData.hasRefunds ? 1 : 0,
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        sx={{
                          bgcolor: 'rgba(16,185,129,0.16)',
                          color: 'success.main',
                          width: 36,
                          height: 36,
                        }}
                      >
                        <CheckIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ letterSpacing: 0.4 }}
                          color="success.main"
                        >
                          {financialData.hasRefunds ? 'Pagado Neto' : 'Pagado'}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 800, color: 'success.dark', lineHeight: 1.2 }}
                        >
                          {formatCurrency(financialData.netPaid)}
                        </Typography>
                      </Box>
                    </Box>
                    {financialData.hasRefunds && (
                      <Box sx={{ pl: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Pagos:
                          </Typography>
                          <Typography variant="caption" fontWeight="600" color="success.dark">
                            +{formatCurrency(financialData.totalPaid)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">
                            Reembolsos:
                          </Typography>
                          <Typography variant="caption" fontWeight="600" color="error.main">
                            -{formatCurrency(financialData.totalRefunded)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </S.DashboardCardGreen>
              </Grid>
              <Grid item xs={12} md={3}>
                <S.DashboardCardRed variant="outlined">
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2.25 }}>
                    <Avatar
                      variant="rounded"
                      sx={{
                        bgcolor: 'rgba(239,68,68,0.16)',
                        color: 'error.main',
                        width: 36,
                        height: 36,
                      }}
                    >
                      <ErrorIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="overline" sx={{ letterSpacing: 0.4 }} color="error">
                        Saldo Pendiente
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 800, color: 'error.dark', lineHeight: 1.2 }}
                      >
                        {formatCurrency(financialData.remaining)}
                      </Typography>
                    </Box>
                  </CardContent>
                </S.DashboardCardRed>
              </Grid>
            </Grid>

            <Grid container>
              {/* Piezas */}
              <Grid item xs={12}>
                <S.SectionBox>
                  <S.SectionHeader>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <S.SectionAvatar>
                        <ReceiptIcon sx={{ fontSize: 16 }} />
                      </S.SectionAvatar>
                      <Typography
                        variant="subtitle1"
                        sx={(theme) => ({
                          fontWeight: 700,
                          color:
                            theme.palette.mode === 'dark'
                              ? theme.palette.info.light
                              : theme.palette.info.dark,
                        })}
                      >
                        Piezas ({order.items.length})
                      </Typography>
                    </Box>
                  </S.SectionHeader>

                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={(theme) => ({
                      border: 0,
                      borderRadius: 0,
                      backgroundColor: theme.palette.background.paper,
                      boxShadow: 'none',
                    })}
                  >
                    <Table
                      stickyHeader
                      size="small"
                      sx={(theme) => ({
                        '& tbody tr:nth-of-type(odd)': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? alpha(theme.palette.common.white, 0.03)
                              : 'rgba(2,6,23,0.02)',
                        },
                      })}
                    >
                      <TableHead>
                        <TableRow
                          sx={(theme) => ({
                            bgcolor:
                              theme.palette.mode === 'dark'
                                ? alpha(theme.palette.common.white, 0.06)
                                : theme.palette.grey[50],
                            '& th': { fontWeight: 700, color: 'text.secondary' },
                          })}
                        >
                          <TableCell>Producto</TableCell>
                          <TableCell>Origen</TableCell>
                          {isMixedSale && <TableCell>Solicitud</TableCell>}
                          <TableCell align="center">Ubicación</TableCell>
                          <TableCell align="right">Precio Original</TableCell>
                          <TableCell align="center">Inc. / Desc.</TableCell>
                          <TableCell align="right">Precio Final</TableCell>
                          <TableCell align="center">Entrega</TableCell>
                          <TableCell align="center">Detalle pieza</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {order.items.map((item) => {
                          const discountAmount = calculateProductDiscount(item);
                          const itemHasMarkup = hasProductMarkup(item);
                          const isRemoteItem = item.fulfillmentType === 'REMOTE';
                          const requestCode = requestCodeByItemId.get(item.id);
                          const deliveryCtx = { order, fulfillmentDetail };
                          const originName = getItemOriginLocationName(item, order);
                          const presenceChip = getItemPresenceChipState(item, deliveryCtx);
                          const deliveryChip = getItemDeliveryChipState(item);
                          return (
                            <TableRow key={item.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight="600">
                                    {item.warehouseInventoryProduct.product.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Código: {item.warehouseInventoryProduct.productCodeUnique}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {isRemoteItem ? (
                                  <Tooltip title={`Traída desde ${originName}`}>
                                    <StatusChip label={originName} color="warning" size="small" />
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Sucursal de origen de la pieza">
                                    <StatusChip label={originName} color="default" size="small" />
                                  </Tooltip>
                                )}
                              </TableCell>
                              {isMixedSale && (
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {requestCode ?? '—'}
                                  </Typography>
                                </TableCell>
                              )}
                              <TableCell align="center">
                                <StatusChip
                                  label={presenceChip.label}
                                  color={presenceChip.color}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(item.originalPrice)}
                              </TableCell>
                              <TableCell align="center">
                                {itemHasMarkup || discountAmount > 0 ? (
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      flexWrap: 'wrap',
                                      gap: 0.5,
                                      justifyContent: 'center',
                                    }}
                                  >
                                    {itemHasMarkup && (
                                      <Chip
                                        label={`+${formatMarkupDisplay(
                                          item.markupType,
                                          Number(item.markupValue),
                                          item.originalPrice,
                                        )}`}
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                      />
                                    )}
                                    {discountAmount > 0 && (
                                      <Chip
                                        label={`-${formatDiscountDisplay(
                                          item.discountType,
                                          Number(item.discountValue),
                                          item.adjustedPrice,
                                        )}`}
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="700" color="primary.main">
                                  {formatCurrency(item.finalPrice)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <StatusChip
                                  label={deliveryChip.label}
                                  color={deliveryChip.color}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center" sx={{ py: 0.5 }}>
                                {item.warehouseInventoryProduct?.id != null ? (
                                  <Tooltip title="Detalle pieza">
                                    <ViewButton
                                      size="small"
                                      aria-label="Detalle pieza"
                                      onClick={() =>
                                        void openPieceDetail(item.warehouseInventoryProduct.id)
                                      }
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </ViewButton>
                                  </Tooltip>
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </S.SectionBox>
              </Grid>

              {/* Mixed-branch fulfillment: SOL/TRL logistics for remote-sourced pieces */}
              {isMixedSale && (
                <Grid item xs={12}>
                  <S.SectionBox>
                    <S.SectionHeader>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <S.SectionAvatar>
                          <LocalShippingIcon sx={{ fontSize: 16 }} />
                        </S.SectionAvatar>
                        <Typography
                          variant="subtitle1"
                          sx={(theme) => ({
                            fontWeight: 700,
                            color:
                              theme.palette.mode === 'dark'
                                ? theme.palette.info.light
                                : theme.palette.info.dark,
                          })}
                        >
                          Solicitudes y logística
                          {fulfillmentDetail ? ` (${fulfillmentDetail.groups.length})` : ''}
                        </Typography>
                      </Box>
                    </S.SectionHeader>
                    {isFulfillmentLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : fulfillmentError ? (
                      <Alert severity="error" sx={{ m: 2 }}>
                        {fulfillmentError}
                      </Alert>
                    ) : (
                      <SaleFulfillmentTimeline groups={fulfillmentDetail?.groups ?? []} />
                    )}
                  </S.SectionBox>
                </Grid>
              )}

              {order.charges && order.charges.length > 0 && (
                <Grid item xs={12}>
                  <S.SectionBox>
                    <S.SectionHeader>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <S.SectionAvatar>
                          <ReceiptIcon sx={{ fontSize: 16 }} />
                        </S.SectionAvatar>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Cargos adicionales ({order.charges.length})
                        </Typography>
                      </Box>
                    </S.SectionHeader>
                    <Box sx={{ px: 2, py: 1 }}>
                      {order.charges.map((charge) => (
                        <Box
                          key={charge.id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 1,
                            py: 1.25,
                            borderBottom: 1,
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 0 },
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {charge.categoryLabel}
                            </Typography>
                            {charge.description?.trim() && (
                              <Typography variant="caption" color="text.secondary">
                                {capitalizeSentence(charge.description)}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="body2" fontWeight={700}>
                            {formatCurrency(Number(charge.amount) || 0)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </S.SectionBox>
                </Grid>
              )}

              {/* Historial de cambios de fecha comercial */}
              {(saleDateHistory.length > 0 || saleDateHistoryError != null) && (
                <Grid item xs={12}>
                  <S.PaymentsBox>
                    <S.PaymentsHeader>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          sx={{
                            bgcolor: 'rgba(6,182,212,0.18)',
                            color: 'info.dark',
                            width: 26,
                            height: 26,
                          }}
                        >
                          <EventIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, color: 'info.dark' }}
                        >
                          Historial de fecha de venta
                        </Typography>
                      </Box>
                    </S.PaymentsHeader>
                    <Box sx={{ px: 2, py: 1 }}>
                      {saleDateHistoryError != null ? (
                        <Alert severity="warning" sx={{ mb: 1 }}>
                          {saleDateHistoryError}
                        </Alert>
                      ) : null}
                      {saleDateHistory.map((entry) => {
                        const performedBy = entry.performedBy;
                        const location = entry.location;
                        const actorRaw =
                          `${performedBy?.firstName ?? ''} ${performedBy?.lastName ?? ''}`.trim();
                        const actorName = actorRaw !== '' ? actorRaw : '—';
                        return (
                          <Box
                            key={entry.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 1.5,
                              py: 1.25,
                              borderBottom: 1,
                              borderColor: 'divider',
                              '&:last-child': { borderBottom: 0 },
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={700}>
                                Fecha comercial actualizada
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {formatRegistrationDateTime(entry.previousSaleDate)} →{' '}
                                {formatRegistrationDateTime(entry.newSaleDate)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Por: {actorName}
                                {location?.name != null && location.name !== ''
                                  ? ` · ${location.name}`
                                  : ''}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                              {formatDate(entry.performedAt)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </S.PaymentsBox>
                </Grid>
              )}

              {/* Historial de pago (pagos, devoluciones y cuotas) */}
              <Grid item xs={12}>
                {/* Payment History */}
                <>
                  {/* Regular Payments */}
                  <S.PaymentsBox>
                    <S.PaymentsHeader>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          sx={{
                            bgcolor: 'rgba(16,185,129,0.18)',
                            color: 'success.dark',
                            width: 26,
                            height: 26,
                          }}
                        >
                          <PaymentIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, color: 'success.dark' }}
                        >
                          Historial de Pagos
                        </Typography>
                      </Box>
                    </S.PaymentsHeader>
                    <Box sx={{ px: 2, py: 1 }}>
                      {financialData.regularPayments.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No hay pagos registrados.
                        </Typography>
                      ) : (
                        <Box>
                          {financialData.regularPayments.map((payment) => {
                            const noteText = getDisplayablePaymentNotes(payment.paymentNotes) ?? '';
                            const hasNotes = noteText.length > 0;
                            const processedByLabel =
                              `${payment.processedBy?.firstName ?? ''} ${payment.processedBy?.lastName ?? ''}`.trim() ||
                              payment.processedBy?.email ||
                              payment.processedBy?.username ||
                              '—';

                            return (
                              <Box
                                key={payment.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'space-between',
                                  py: 1.25,
                                  borderBottom: 1,
                                  borderColor: 'divider',
                                  '&:last-child': { borderBottom: 0 },
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      bgcolor: (theme) =>
                                        alpha(
                                          theme.palette.success.main,
                                          theme.palette.mode === 'dark' ? 0.2 : 0.12,
                                        ),
                                      color: 'success.main',
                                    }}
                                  >
                                    <PaymentIcon fontSize="small" />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight="700">
                                      {formatCurrency(Number(payment.paymentAmount) || 0)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {getPaymentMethodLabel(payment.paymentMethod)} •{' '}
                                      {formatDate(payment.paymentDate)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block' }}
                                    >
                                      Registrado por: {processedByLabel}
                                    </Typography>
                                    {hasNotes && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
                                      >
                                        {noteText}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  </S.PaymentsBox>

                  {/* Refunds History */}
                  {financialData.refundPayments.length > 0 && (
                    <S.RefundsBox>
                      <S.RefundsHeader>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar
                            sx={{
                              bgcolor: 'rgba(239,68,68,0.18)',
                              color: 'error.dark',
                              width: 26,
                              height: 26,
                            }}
                          >
                            <UndoIcon sx={{ fontSize: 16 }} />
                          </Avatar>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, color: 'error.dark' }}
                          >
                            Historial de Devoluciones
                          </Typography>
                        </Box>
                      </S.RefundsHeader>
                      <Box sx={{ px: 2, py: 1 }}>
                        {financialData.refundPayments.map((refund) => {
                          const refundAmount = Math.abs(Number(refund.paymentAmount) || 0);
                          const noteText = getDisplayablePaymentNotes(refund.paymentNotes) ?? '';
                          const hasNotes = noteText.length > 0;
                          const processedByLabel =
                            `${refund.processedBy?.firstName ?? ''} ${refund.processedBy?.lastName ?? ''}`.trim() ||
                            refund.processedBy?.email ||
                            refund.processedBy?.username ||
                            '—';

                          return (
                            <Box
                              key={refund.id}
                              sx={{
                                py: 1.5,
                                borderBottom: 1,
                                borderColor: 'divider',
                                '&:last-child': { borderBottom: 0 },
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'space-between',
                                  mb: hasNotes ? 1 : 0,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      bgcolor: (theme) =>
                                        alpha(
                                          theme.palette.error.main,
                                          theme.palette.mode === 'dark' ? 0.2 : 0.12,
                                        ),
                                      color: 'error.main',
                                    }}
                                  >
                                    <UndoIcon fontSize="small" />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight="700" color="error.main">
                                      -{formatCurrency(refundAmount)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {getPaymentMethodLabel(refund.paymentMethod)} •{' '}
                                      {formatDate(refund.paymentDate)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block' }}
                                    >
                                      Registrado por: {processedByLabel}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </S.RefundsBox>
                  )}
                </>

                {/* Installments */}
                {installments.length > 0 && (
                  <Box
                    sx={(theme) => ({
                      border: 1,
                      borderColor: alpha('#9333ea', theme.palette.mode === 'dark' ? 0.4 : 0.25),
                      borderRadius: 2,
                      overflow: 'hidden',
                      backgroundColor: theme.palette.background.paper,
                    })}
                  >
                    <Box
                      sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1.25,
                        background:
                          theme.palette.mode === 'dark'
                            ? alpha('#9333ea', 0.14)
                            : 'linear-gradient(90deg, rgba(147,51,234,0.10) 0%, rgba(147,51,234,0.04) 100%)',
                        borderBottom: `1px solid ${alpha('#9333ea', theme.palette.mode === 'dark' ? 0.35 : 0.25)}`,
                      })}
                    >
                      <Avatar
                        sx={(theme) => ({
                          bgcolor: alpha('#9333ea', theme.palette.mode === 'dark' ? 0.22 : 0.18),
                          color: theme.palette.mode === 'dark' ? '#c4b5fd' : '#7c3aed',
                          width: 26,
                          height: 26,
                        })}
                      >
                        <ScheduleIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Typography
                        variant="subtitle1"
                        sx={(theme) => ({
                          fontWeight: 700,
                          color: theme.palette.mode === 'dark' ? '#c4b5fd' : '#6d28d9',
                        })}
                      >
                        Plan de Cuotas
                      </Typography>
                    </Box>
                    <Box sx={{ px: 2, py: 1 }}>
                      {installments.map((installment) => (
                        <Box
                          key={installment.id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 1,
                            borderBottom: 1,
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 0 },
                          }}
                        >
                          <Box>
                            <Typography variant="body2">
                              Cuota {installment.installmentNumber}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Vence: {formatDate(installment.dueDate)}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight="600">
                              {formatCurrency(installment.amount)}
                            </Typography>
                            <Chip
                              label={installment.status}
                              size="small"
                              color={
                                installment.status === 'PAID'
                                  ? 'success'
                                  : installment.status === 'OVERDUE'
                                    ? 'error'
                                    : 'default'
                              }
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Historial de piezas (cambios posteriores: intercambios, devoluciones) */}
                {order && (
                  <ProductHistorySection
                    orderId={order.id}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                    onViewExchangedOutPiece={(wipId, finalPrice) =>
                      void openPieceDetail(wipId, finalPrice)
                    }
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        ) : null}
      </DetailDrawer>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 2 }}
        open={pieceDetailFetching}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog
        open={Boolean(pieceDetailFetchError)}
        onClose={() => setPieceDetailFetchError(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Alert severity="error">{pieceDetailFetchError}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPieceDetailFetchError(null)} color="inherit">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deliverError)}
        onClose={() => setDeliverError(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Alert severity="error">{deliverError}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliverError(null)} color="inherit">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <DeliverPiecesModal
        open={showDeliverModal}
        order={order}
        fulfillmentDetail={fulfillmentDetail}
        onClose={() => {
          if (!isDelivering) setShowDeliverModal(false);
        }}
        onConfirm={(items) => void handleDeliverPieces(items)}
        loading={isDelivering}
      />

      <InventoryDetailModal
        open={pieceDetailOpen && Boolean(pieceDetailItem)}
        onClose={closePieceDetailModal}
        item={pieceDetailItem}
        onEditItem={() => {}}
        onSave={() => {
          void reloadPieceDetailFromOrder();
          void refreshOrderDetails();
        }}
        onItemUpdate={(updated) => setPieceDetailItem(updated)}
        saleContext={pieceDetailSaleContext}
      />

      {order != null && (
        <EditSaleDateModal
          open={editSaleDateOpen}
          onClose={() => {
            if (!editSaleDateLoading) {
              setEditSaleDateOpen(false);
              clearEditSaleDateError();
            }
          }}
          orderCode={order.orderCode}
          saleDate={order.saleDate}
          createdAt={order.createdAt}
          loading={editSaleDateLoading}
          error={editSaleDateError}
          onSubmit={handleSaveSaleDate}
        />
      )}
    </>
  );
};

export default OrderDetailsModal;
